import React, { useState, useEffect } from 'react';
import { MdClose, MdBusiness, MdEdit, MdPerson, MdPhone, MdLocationOn, MdGroup } from 'react-icons/md';
import axios from 'axios';
import SearchableDropdown from './SearchableDropdown';

const EditTraderModal = ({ isOpen, onClose, showPopup, fetchData, API_BASE_URL, editTrader, groups = [], mmdas = [], zones = [], nationalIdTypes = [] }) => {
  const [formData, setFormData] = useState({
    first_name: '',
    middle_name: '',
    last_name: '',
    phone: '',
    national_id_type: '',
    national_info: '',
    business_name: '',
    status: '',
    group_id: '',
    mmda_id: '',
    zone_id: '',
  });

   const [formErrors, setFormErrors] = useState({});
   const [isSubmitting, setIsSubmitting] = useState(false);
   const [localGroups, setLocalGroups] = useState([]);
   const [localMmdas, setLocalMmdas] = useState([]);
   const [localZones, setLocalZones] = useState([]);
   const [localNationalIdTypes, setLocalNationalIdTypes] = useState([]);

   // Fetch dropdown data when modal opens if not already available
   useEffect(() => {
     const fetchDropdownData = async () => {
       if (!isOpen) return;

       try {
         const token = localStorage.getItem('authToken');
         const config = token ? { headers: { Authorization: `Bearer ${token}` } } : {};

         // Only fetch if data is not already provided or is empty
         const promises = [];

         if (!groups || groups.length === 0) {
           promises.push(axios.get(`${API_BASE_URL}/zonal-groups`, config));
         } else {
           promises.push(Promise.resolve({ data: groups }));
         }

         if (!mmdas || mmdas.length === 0) {
           promises.push(axios.get(`${API_BASE_URL}/mmdas`, config));
         } else {
           promises.push(Promise.resolve({ data: mmdas }));
         }

         if (!zones || zones.length === 0) {
           promises.push(axios.get(`${API_BASE_URL}/zones`, config));
         } else {
           promises.push(Promise.resolve({ data: zones }));
         }

         if (!nationalIdTypes || nationalIdTypes.length === 0) {
           promises.push(axios.get(`${API_BASE_URL}/national-ids`, config));
         } else {
           promises.push(Promise.resolve({ data: nationalIdTypes }));
         }

         const [groupsRes, mmdasRes, zonesRes, nationalIdTypesRes] = await Promise.all(promises);

         setLocalGroups(groupsRes.data || []);
         setLocalMmdas(mmdasRes.data || []);
         setLocalZones(zonesRes.data || []);
         setLocalNationalIdTypes(nationalIdTypesRes.data || []);

         console.log('Fetched dropdown data:');
         console.log('groups:', groupsRes.data);
         console.log('mmdas:', mmdasRes.data);
         console.log('zones:', zonesRes.data);
         console.log('nationalIdTypes:', nationalIdTypesRes.data);

       } catch (error) {
         console.error('Error fetching dropdown data:', error);
         showPopup('Failed to load dropdown data', 'error');
       }
     };

     fetchDropdownData();
   }, [isOpen, API_BASE_URL, groups, mmdas, zones, nationalIdTypes, showPopup]);

   useEffect(() => {
     if (editTrader) {
       setFormData({
         first_name: editTrader.first_name || '',
         middle_name: editTrader.middle_name || '',
         last_name: editTrader.last_name || '',
         phone: editTrader.phone || '',
         national_id_type: editTrader.id_type || '', // Store the ID, not the string
         national_info: editTrader.national_info || '',
         business_name: editTrader.business_name || '',
         status: editTrader.status && editTrader.status.toLowerCase() === 'inactive' ? 'inactive' : 'active',
         group_id: editTrader.group_id || null,
         mmda_id: editTrader.mmda_id || null,
         zone_id: editTrader.zone_id || null,
       });
     }
   }, [editTrader]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleDropdownChange = (name, value) => {
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.first_name) errors.first_name = 'First Name is required';
    if (!formData.last_name) errors.last_name = 'Last Name is required';
    if (!formData.phone) errors.phone = 'Phone is required';
    if (!formData.national_id_type) errors.national_id_type = 'ID Type is required';
    if (!formData.national_info) errors.national_info = 'National Info is required';
    if (!formData.business_name) errors.business_name = 'Business Name is required';
    if (!formData.group_id) errors.group_id = 'Group is required';
    if (!formData.mmda_id) errors.mmda_id = 'MMDA is required';
    if (!formData.zone_id) errors.zone_id = 'Zone is required';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      showPopup('Please fill in all required fields.', 'error');
      return;
    }

    setIsSubmitting(true);

    try {
      const updatedFormData = {
        ...formData,
        id_type: formData.national_id_type === '' ? null : parseInt(formData.national_id_type),
        // Convert empty strings to null for integer fields
        zone_id: formData.zone_id === '' ? null : parseInt(formData.zone_id),
        group_id: formData.group_id === '' ? null : parseInt(formData.group_id),
        mmda_id: formData.mmda_id === '' ? null : parseInt(formData.mmda_id),
      };

      const token = localStorage.getItem('authToken');
      const config = token ? { headers: { Authorization: `Bearer ${token}` } } : {};

      if (!API_BASE_URL) {
        console.error("API_BASE_URL is undefined. Please check your .env file and server configuration.");
        showPopup('Failed to update trader: API_BASE_URL is not defined.', 'error');
        return;
      }

      console.log('Sending update data:', updatedFormData);
      await axios.put(`${API_BASE_URL}/traders/${editTrader.trader_id}`, updatedFormData, config);

      showPopup('Trader updated successfully!', 'success');
      fetchData();
      handleClose();
    } catch (err) {
      if (err.response) {
        showPopup(`Failed to update trader: ${err.response.data.error || 'Unknown error'}`, 'error');
      } else {
        showPopup('Failed to update trader. Please try again.', 'error');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setFormData({
      first_name: '',
      middle_name: '',
      last_name: '',
      phone: '',
      national_id_type: '',
      national_info: '',
      business_name: '',
      status: '',
      group_id: '',
      mmda_id: '',
      zone_id: '',
    });
    setFormErrors({});
    setIsSubmitting(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      width: '100vw',
      height: '100vh',
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1000,
      backdropFilter: 'blur(4px)'
    }} onClick={handleClose}>
      <div style={{
        backgroundColor: '#ffffff',
        borderRadius: 16,
        padding: 0,
        width: '90%',
        maxWidth: 700,
        maxHeight: '90vh',
        overflow: 'auto',
        boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
        transform: 'scale(1)',
        transition: 'all 0.3s ease'
      }} onClick={(e) => e.stopPropagation()}>

        {/* Header */}
        <div style={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          padding: '24px 32px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <MdEdit size={28} />
            <h2 style={{ margin: 0, fontSize: 24, fontWeight: '600' }}>Edit Trader</h2>
          </div>
          <button
            onClick={handleClose}
            style={{
              background: 'rgba(255, 255, 255, 0.2)',
              border: 'none',
              borderRadius: 8,
              padding: 8,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'background-color 0.2s ease'
            }}
            onMouseEnter={e => e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.3)'}
            onMouseLeave={e => e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.2)'}
          >
            <MdClose size={20} color="white" />
          </button>
        </div>

        {/* Form Content */}
        <form onSubmit={handleSubmit} style={{ padding: '32px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>

            {/* First Name Field */}
            <div>
              <label style={{
                display: 'block',
                marginBottom: 8,
                fontWeight: '600',
                color: '#374151',
                fontSize: 14
              }}>
                <MdPerson size={16} style={{ marginRight: 6, verticalAlign: 'middle' }} />
                First Name *
              </label>
              <input
                type="text"
                name="first_name"
                value={formData.first_name}
                onChange={handleChange}
                placeholder="Enter first name"
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  border: formErrors.first_name ? '2px solid #ef4444' : '2px solid #e5e7eb',
                  borderRadius: 8,
                  fontSize: 14,
                  outline: 'none',
                  transition: 'border-color 0.2s ease',
                  backgroundColor: '#fafafa'
                }}
                onFocus={e => {
                  if (!formErrors.first_name) {
                    e.currentTarget.style.borderColor = '#667eea';
                    e.currentTarget.style.backgroundColor = '#ffffff';
                  }
                }}
                onBlur={e => {
                  if (!formErrors.first_name) {
                    e.currentTarget.style.borderColor = '#e5e7eb';
                    e.currentTarget.style.backgroundColor = '#fafafa';
                  }
                }}
              />
              {formErrors.first_name && (
                <p style={{ color: '#ef4444', fontSize: 12, margin: '4px 0 0 0' }}>
                  {formErrors.first_name}
                </p>
              )}
            </div>

            {/* Last Name Field */}
            <div>
              <label style={{
                display: 'block',
                marginBottom: 8,
                fontWeight: '600',
                color: '#374151',
                fontSize: 14
              }}>
                <MdPerson size={16} style={{ marginRight: 6, verticalAlign: 'middle' }} />
                Last Name *
              </label>
              <input
                type="text"
                name="last_name"
                value={formData.last_name}
                onChange={handleChange}
                placeholder="Enter last name"
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  border: formErrors.last_name ? '2px solid #ef4444' : '2px solid #e5e7eb',
                  borderRadius: 8,
                  fontSize: 14,
                  outline: 'none',
                  transition: 'border-color 0.2s ease',
                  backgroundColor: '#fafafa'
                }}
                onFocus={e => {
                  if (!formErrors.last_name) {
                    e.currentTarget.style.borderColor = '#667eea';
                    e.currentTarget.style.backgroundColor = '#ffffff';
                  }
                }}
                onBlur={e => {
                  if (!formErrors.last_name) {
                    e.currentTarget.style.borderColor = '#e5e7eb';
                    e.currentTarget.style.backgroundColor = '#fafafa';
                  }
                }}
              />
              {formErrors.last_name && (
                <p style={{ color: '#ef4444', fontSize: 12, margin: '4px 0 0 0' }}>
                  {formErrors.last_name}
                </p>
              )}
            </div>

            {/* Middle Name Field */}
            <div style={{ gridColumn: '1 / -1' }}>
              <label style={{
                display: 'block',
                marginBottom: 8,
                fontWeight: '600',
                color: '#374151',
                fontSize: 14
              }}>
                <MdPerson size={16} style={{ marginRight: 6, verticalAlign: 'middle' }} />
                Middle Name
              </label>
              <input
                type="text"
                name="middle_name"
                value={formData.middle_name}
                onChange={handleChange}
                placeholder="Enter middle name (optional)"
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  border: '2px solid #e5e7eb',
                  borderRadius: 8,
                  fontSize: 14,
                  outline: 'none',
                  transition: 'border-color 0.2s ease',
                  backgroundColor: '#fafafa'
                }}
                onFocus={e => {
                  e.currentTarget.style.borderColor = '#667eea';
                  e.currentTarget.style.backgroundColor = '#ffffff';
                }}
                onBlur={e => {
                  e.currentTarget.style.borderColor = '#e5e7eb';
                  e.currentTarget.style.backgroundColor = '#fafafa';
                }}
              />
            </div>

            {/* Phone Field */}
            <div>
              <label style={{
                display: 'block',
                marginBottom: 8,
                fontWeight: '600',
                color: '#374151',
                fontSize: 14
              }}>
                <MdPhone size={16} style={{ marginRight: 6, verticalAlign: 'middle' }} />
                Phone Number *
              </label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                placeholder="Enter phone number"
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  border: formErrors.phone ? '2px solid #ef4444' : '2px solid #e5e7eb',
                  borderRadius: 8,
                  fontSize: 14,
                  outline: 'none',
                  transition: 'border-color 0.2s ease',
                  backgroundColor: '#fafafa'
                }}
                onFocus={e => {
                  if (!formErrors.phone) {
                    e.currentTarget.style.borderColor = '#667eea';
                    e.currentTarget.style.backgroundColor = '#ffffff';
                  }
                }}
                onBlur={e => {
                  if (!formErrors.phone) {
                    e.currentTarget.style.borderColor = '#e5e7eb';
                    e.currentTarget.style.backgroundColor = '#fafafa';
                  }
                }}
              />
              {formErrors.phone && (
                <p style={{ color: '#ef4444', fontSize: 12, margin: '4px 0 0 0' }}>
                  {formErrors.phone}
                </p>
              )}
            </div>

            {/* Business Name Field */}
            <div>
              <label style={{
                display: 'block',
                marginBottom: 8,
                fontWeight: '600',
                color: '#374151',
                fontSize: 14
              }}>
                <MdBusiness size={16} style={{ marginRight: 6, verticalAlign: 'middle' }} />
                Business Name *
              </label>
              <input
                type="text"
                name="business_name"
                value={formData.business_name}
                onChange={handleChange}
                placeholder="Enter business name"
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  border: formErrors.business_name ? '2px solid #ef4444' : '2px solid #e5e7eb',
                  borderRadius: 8,
                  fontSize: 14,
                  outline: 'none',
                  transition: 'border-color 0.2s ease',
                  backgroundColor: '#fafafa'
                }}
                onFocus={e => {
                  if (!formErrors.business_name) {
                    e.currentTarget.style.borderColor = '#667eea';
                    e.currentTarget.style.backgroundColor = '#ffffff';
                  }
                }}
                onBlur={e => {
                  if (!formErrors.business_name) {
                    e.currentTarget.style.borderColor = '#e5e7eb';
                    e.currentTarget.style.backgroundColor = '#fafafa';
                  }
                }}
              />
              {formErrors.business_name && (
                <p style={{ color: '#ef4444', fontSize: 12, margin: '4px 0 0 0' }}>
                  {formErrors.business_name}
                </p>
              )}
            </div>
            {/* National ID Type Field */}
            <div>
              <label style={{
                display: 'block',
                marginBottom: 8,
                fontWeight: '600',
                color: '#374151',
                fontSize: 14
              }}>
                National ID Type *
              </label>
              <div style={{ position: 'relative' }}>
                <SearchableDropdown
                  options={Array.isArray(localNationalIdTypes) ? localNationalIdTypes.map((idType) => ({
                    value: idType.id,
                    label: idType.id_type,
                  })) : []}
                  value={formData.national_id_type}
                  onChange={(value) => handleDropdownChange('national_id_type', value)}
                  placeholder="Select ID Type"
                />
              </div>
              {formErrors.national_id_type && (
                <p style={{ color: '#ef4444', fontSize: 12, margin: '4px 0 0 0' }}>
                  {formErrors.national_id_type}
                </p>
              )}
            </div>

            {/* National Info Field */}
            <div>
              <label style={{
                display: 'block',
                marginBottom: 8,
                fontWeight: '600',
                color: '#374151',
                fontSize: 14
              }}>
                National ID Number *
              </label>
              <input
                type="text"
                name="national_info"
                value={formData.national_info}
                onChange={handleChange}
                placeholder="Enter national ID number"
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  border: formErrors.national_info ? '2px solid #ef4444' : '2px solid #e5e7eb',
                  borderRadius: 8,
                  fontSize: 14,
                  outline: 'none',
                  transition: 'border-color 0.2s ease',
                  backgroundColor: '#fafafa'
                }}
                onFocus={e => {
                  if (!formErrors.national_info) {
                    e.currentTarget.style.borderColor = '#667eea';
                    e.currentTarget.style.backgroundColor = '#ffffff';
                  }
                }}
                onBlur={e => {
                  if (!formErrors.national_info) {
                    e.currentTarget.style.borderColor = '#e5e7eb';
                    e.currentTarget.style.backgroundColor = '#fafafa';
                  }
                }}
              />
              {formErrors.national_info && (
                <p style={{ color: '#ef4444', fontSize: 12, margin: '4px 0 0 0' }}>
                  {formErrors.national_info}
                </p>
              )}
            </div>

            {/* Group Field */}
            <div>
              <label style={{
                display: 'block',
                marginBottom: 8,
                fontWeight: '600',
                color: '#374151',
                fontSize: 14
              }}>
                <MdGroup size={16} style={{ marginRight: 6, verticalAlign: 'middle' }} />
                Group *
              </label>
              <div style={{ position: 'relative' }}>
                <SearchableDropdown
                  options={Array.isArray(localGroups) ? localGroups.map((group) => ({
                    value: group.group_id,
                    label: group.group_name,
                  })) : []}
                  value={formData.group_id}
                  onChange={(value) => handleDropdownChange('group_id', value)}
                  placeholder="Select Group"
                />
              </div>
              {formErrors.group_id && (
                <p style={{ color: '#ef4444', fontSize: 12, margin: '4px 0 0 0' }}>
                  {formErrors.group_id}
                </p>
              )}
            </div>

            {/* MMDA Field */}
            <div>
              <label style={{
                display: 'block',
                marginBottom: 8,
                fontWeight: '600',
                color: '#374151',
                fontSize: 14
              }}>
                <MdBusiness size={16} style={{ marginRight: 6, verticalAlign: 'middle' }} />
                MMDA *
              </label>
              <div style={{ position: 'relative' }}>
                <SearchableDropdown
                  options={Array.isArray(localMmdas) ? localMmdas.map((mmda) => ({
                    value: mmda.mmda_id,
                    label: mmda.name,
                  })) : []}
                  value={formData.mmda_id}
                  onChange={(value) => handleDropdownChange('mmda_id', value)}
                  placeholder="Select MMDA"
                />
              </div>
              {formErrors.mmda_id && (
                <p style={{ color: '#ef4444', fontSize: 12, margin: '4px 0 0 0' }}>
                  {formErrors.mmda_id}
                </p>
              )}
            </div>

            {/* Zone Field */}
            <div>
              <label style={{
                display: 'block',
                marginBottom: 8,
                fontWeight: '600',
                color: '#374151',
                fontSize: 14
              }}>
                <MdLocationOn size={16} style={{ marginRight: 6, verticalAlign: 'middle' }} />
                Zone *
              </label>
              <div style={{ position: 'relative' }}>
                <SearchableDropdown
                  options={Array.isArray(localZones) ? localZones.map((zone) => ({
                    value: zone.zone_id,
                    label: zone.zone_name,
                  })) : []}
                  value={formData.zone_id}
                  onChange={(value) => handleDropdownChange('zone_id', value)}
                  placeholder="Select Zone"
                />
              </div>
              {formErrors.zone_id && (
                <p style={{ color: '#ef4444', fontSize: 12, margin: '4px 0 0 0' }}>
                  {formErrors.zone_id}
                </p>
              )}
            </div>

            {/* Status Field */}
            <div>
              <label style={{
                display: 'block',
                marginBottom: 8,
                fontWeight: '600',
                color: '#374151',
                fontSize: 14
              }}>
                Status
              </label>
              <select
                name="status"
                value={formData.status}
                onChange={handleChange}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  border: '2px solid #e5e7eb',
                  borderRadius: 8,
                  fontSize: 14,
                  outline: 'none',
                  transition: 'border-color 0.2s ease',
                  backgroundColor: '#fafafa',
                  cursor: 'pointer'
                }}
                onFocus={e => {
                  e.currentTarget.style.borderColor = '#667eea';
                  e.currentTarget.style.backgroundColor = '#ffffff';
                }}
                onBlur={e => {
                  e.currentTarget.style.borderColor = '#e5e7eb';
                  e.currentTarget.style.backgroundColor = '#fafafa';
                }}
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          </div>

          {/* Action Buttons */}
          <div style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 12,
            marginTop: 32,
            paddingTop: 24,
            borderTop: '1px solid #e5e7eb'
          }}>
            <button
              type="button"
              onClick={handleClose}
              disabled={isSubmitting}
              style={{
                padding: '12px 24px',
                border: '2px solid #e5e7eb',
                borderRadius: 8,
                backgroundColor: '#ffffff',
                color: '#374151',
                fontSize: 14,
                fontWeight: '600',
                cursor: isSubmitting ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s ease',
                opacity: isSubmitting ? 0.6 : 1
              }}
              onMouseEnter={e => {
                if (!isSubmitting) {
                  e.currentTarget.style.backgroundColor = '#f9fafb';
                  e.currentTarget.style.borderColor = '#d1d5db';
                }
              }}
              onMouseLeave={e => {
                if (!isSubmitting) {
                  e.currentTarget.style.backgroundColor = '#ffffff';
                  e.currentTarget.style.borderColor = '#e5e7eb';
                }
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              style={{
                padding: '12px 24px',
                border: 'none',
                borderRadius: 8,
                background: isSubmitting
                  ? '#9ca3af'
                  : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                fontSize: 14,
                fontWeight: '600',
                cursor: isSubmitting ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s ease',
                display: 'flex',
                alignItems: 'center',
                gap: 8
              }}
              onMouseEnter={e => {
                if (!isSubmitting) {
                  e.currentTarget.style.transform = 'translateY(-1px)';
                  e.currentTarget.style.boxShadow = '0 8px 25px rgba(102, 126, 234, 0.4)';
                }
              }}
              onMouseLeave={e => {
                if (!isSubmitting) {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                }
              }}
            >
              <MdEdit size={16} />
              {isSubmitting ? 'Updating...' : 'Update Trader'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditTraderModal;
